// Floor options admin data access — wraps the contract-scoped
// floor-options endpoints under `apps/backend/src/admin/floor-options/**`:
//   GET    /admin/floor-options
//   POST   /admin/floor-options
//   PUT    /admin/floor-options/:id
//   PUT    /admin/floor-options/reorder
//   DELETE /admin/floor-options/:id
//
// Response and input shapes mirror the backend contracts. They are
// redeclared here rather than imported because the monorepo
// dependency rules forbid a frontend→backend edge (AGENTS.md
// §Monorepo Structure).

import { ApiError, apiFetch } from './api'

// ----- DTOs — mirror backend response shapes verbatim -------------

export interface FloorOptionRow {
  id: string
  name: string
  displayOrder: number
}

export interface ListFloorOptionsResponse {
  floorOptions: FloorOptionRow[]
}

export interface FloorOptionCreateInput {
  name: string
}

export interface FloorOptionUpdateInput {
  name: string
}

export interface FloorOptionReorderInput {
  orderedIds: string[]
}

export type CreatedFloorOption = FloorOptionRow

export type UpdatedFloorOption = FloorOptionRow

// ----- Domain errors -----------------------------------------------

// Surfaces the contract-scoped uniqueness constraint on floor-option
// names. Mapped from a backend `409 already_exists` so the page can
// keep the dialog open and render the conflict message inline as a
// form-level error rather than tearing down the user's input.
export class FloorOptionConflictError extends Error {
  readonly status: number
  readonly code: string

  constructor(
    public readonly attemptedName: string,
    public readonly cause?: ApiError,
  ) {
    super(
      cause?.message ??
        `A floor named "${attemptedName}" already exists on this contract`,
    )
    this.name = 'FloorOptionConflictError'
    this.status = 409
    this.code = 'already_exists'
  }
}

// ----- Error mapping helpers ---------------------------------------

function isAlreadyExists(err: ApiError): boolean {
  return err.status === 409
}

// ----- Read endpoints -----------------------------------------------

export function fetchFloorOptions(): Promise<ListFloorOptionsResponse> {
  return apiFetch<ListFloorOptionsResponse>('/admin/floor-options')
}

// ----- Write endpoints ----------------------------------------------

export async function createFloorOption(
  input: FloorOptionCreateInput,
): Promise<CreatedFloorOption> {
  try {
    return await apiFetch<CreatedFloorOption>('/admin/floor-options', {
      method: 'POST',
      json: input,
    })
  } catch (err) {
    if (err instanceof ApiError && isAlreadyExists(err)) {
      throw new FloorOptionConflictError(input.name, err)
    }
    throw err
  }
}

export async function updateFloorOption(
  id: string,
  input: FloorOptionUpdateInput,
): Promise<UpdatedFloorOption> {
  try {
    return await apiFetch<UpdatedFloorOption>(
      `/admin/floor-options/${encodeURIComponent(id)}`,
      { method: 'PUT', json: input },
    )
  } catch (err) {
    if (err instanceof ApiError && isAlreadyExists(err)) {
      throw new FloorOptionConflictError(input.name, err)
    }
    throw err
  }
}

export function deleteFloorOption(
  id: string,
): Promise<{ deleted: true }> {
  return apiFetch<{ deleted: true }>(
    `/admin/floor-options/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
}

// Bulk-rewrite the picker order for the active contract. The backend
// validates the submitted set against what is in the DB and rejects
// payloads that don't match exactly (extras / missing / duplicates),
// so the FE can apply optimistic updates safely as long as it falls
// back to the response on error.
export function reorderFloorOptions(
  orderedIds: string[],
): Promise<ListFloorOptionsResponse> {
  return apiFetch<ListFloorOptionsResponse>(
    '/admin/floor-options/reorder',
    { method: 'PUT', json: { orderedIds } satisfies FloorOptionReorderInput },
  )
}

// ----- Capture-side resolve ----------------------------------------

// Maps a chosen floor-option name to a per-building `floors` row id
// at write time, creating the row lazily on first use at this
// building. The capture flow uses this between the user picking an
// option in the New Room dialog and the existing
// /capture/sessions/:id/spaces request — see
// `apps/backend/src/capture/resolve-floor.ts`.
//
// Keyed off the active capture session (not a bare buildingId) so
// the server enforces the same ownership / state checks as the
// downstream createSpace request — owned by the caller, building-
// scoped, not completed (Codex P2 on PR #421).
export interface ResolvedFloor {
  floorId: string
  floorName: string
}

export function resolveFloorOption(
  sessionId: string,
  floorName: string,
): Promise<ResolvedFloor> {
  return apiFetch<ResolvedFloor>('/capture/floors/resolve', {
    method: 'POST',
    json: { sessionId, floorName },
  })
}
